"use client";

import type {
  GridColumnCount,
  GridColumnLayout,
} from "@homeinstead/ui/components/Grid";
import type { ReactElement, ReactNode } from "react";
import type { EnabledGridOverflow } from "~www/types/enabled-grid-overflow";

import { Button } from "@homeinstead/ui/components/Button";
import { Grid as UIGrid } from "@homeinstead/ui/components/Grid";
import { cn } from "@homeinstead/ui/utils/classnames";
import { Children, isValidElement, useId, useState } from "react";
import { useOverflowRevealAnimation } from "~www/hooks/use-overflow-reveal-animation";
import { useOverflowRevealFocus } from "~www/hooks/use-overflow-reveal-focus";

type GridOverflowProps = EnabledGridOverflow & {
  children: ReactNode;
  columnCount?: GridColumnCount;
  columnLayout?: GridColumnLayout;
};

type OverflowCapacities = {
  large: number;
  medium: number;
  small: number;
};

const ITEM_TRANSITION_CLASS_NAME =
  "motion-safe:transition-[opacity,transform] motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none";

const coalesceColumnCount = (
  ...candidates: Array<GridColumnCount | undefined>
): GridColumnCount => {
  for (const candidate of candidates) {
    if (candidate !== undefined) {
      return candidate;
    }
  }

  return 1;
};

const getOverflowCapacities = ({
  columnCount,
  columnLayout,
  rowCap,
}: Pick<
  GridOverflowProps,
  "columnCount" | "columnLayout" | "rowCap"
>): OverflowCapacities => {
  if (!columnLayout) {
    const mediumColumns = columnCount ?? 2;

    return {
      large: rowCap * mediumColumns,
      medium: rowCap * mediumColumns,
      small: rowCap,
    };
  }

  const smallColumns = coalesceColumnCount(columnLayout.smallViewport);
  const mediumColumns = coalesceColumnCount(
    columnLayout.mediumViewport,
    columnLayout.smallViewport,
    columnCount,
  );
  const largeColumns = coalesceColumnCount(
    columnLayout.largeViewport,
    columnLayout.mediumViewport,
    columnLayout.smallViewport,
  );

  return {
    large: rowCap * largeColumns,
    medium: rowCap * mediumColumns,
    small: rowCap * smallColumns,
  };
};

type GridOverflowItemsProps = {
  capacities: OverflowCapacities;
  expanded: boolean;
  isRevealVisible: boolean;
  items: ReactElement[];
};

/**
 * Apply the collapsed "from" state only at breakpoints where CSS had hidden
 * the item. Using `smallCapacity` alone would flash already-visible md/xl
 * items to opacity-0 for one paint before the reveal frame.
 *
 * Breakpoints match `GridOverflowStyles` (md = 768px, xl = 1280px).
 */
const REVEAL_VIEWPORT_PREFIXES = [
  { capacityKey: "large", prefix: "xl:" },
  { capacityKey: "medium", prefix: "md:" },
  { capacityKey: "small", prefix: "" },
] as const;

const getRevealViewportClassName = (
  prefix: "" | "md:" | "xl:",
  isHidden: boolean,
) =>
  isHidden
    ? `${prefix}translate-y-2 ${prefix}opacity-0`
    : `${prefix}translate-y-0 ${prefix}opacity-100`;

const getRevealAnimationClassName = ({
  capacities,
  index,
  shouldAnimateIn,
}: {
  capacities: OverflowCapacities;
  index: number;
  shouldAnimateIn: boolean;
}) => {
  if (!shouldAnimateIn) {
    return "translate-y-0 opacity-100";
  }

  return cn(
    ...REVEAL_VIEWPORT_PREFIXES.map(({ capacityKey, prefix }) =>
      getRevealViewportClassName(prefix, index >= capacities[capacityKey]),
    ),
  );
};

const GridOverflowItems = ({
  capacities,
  expanded,
  isRevealVisible,
  items,
}: GridOverflowItemsProps) => {
  return (
    <>
      {items.map((child, index) => {
        const shouldAnimateIn = expanded && !isRevealVisible;

        return (
          <li
            className={cn(
              ITEM_TRANSITION_CLASS_NAME,
              getRevealAnimationClassName({
                capacities,
                index,
                shouldAnimateIn,
              }),
            )}
            key={child.key}
            role="listitem"
          >
            {child}
          </li>
        );
      })}
    </>
  );
};

type GridOverflowRevealControlProps = {
  buttonLabel: string;
  capacities: OverflowCapacities;
  itemCount: number;
  listId: string;
  onReveal: () => void;
};

const GridOverflowRevealControl = ({
  buttonLabel,
  capacities,
  itemCount,
  listId,
  onReveal,
}: GridOverflowRevealControlProps) => {
  if (itemCount <= capacities.small) {
    return null;
  }

  return (
    <div
      className="pt-8 md:pt-10"
      data-fits-md={itemCount <= capacities.medium ? "" : undefined}
      data-fits-xl={itemCount <= capacities.large ? "" : undefined}
      data-grid-overflow-reveal=""
    >
      <Button
        aria-controls={listId}
        aria-expanded={false}
        onClick={onReveal}
        type="button"
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

type GridOverflowStylesProps = {
  capacities: OverflowCapacities;
  listId: string;
  scopeId: string;
};

/**
 * Prefer scoped `<style>` over Tailwind or `useMediaQuery` for overflow caps:
 * - Caps are dynamic (`nth-child(-n+${capacity})`), which Tailwind cannot
 *   express and which CSS variables cannot drive inside `:nth-child`.
 * - Emitting the same media queries in SSR HTML keeps first paint aligned
 *   with the server markup and avoids hydration CLS from JS breakpoint reads.
 */
const buildViewportCapRules = ({
  capacity,
  listId,
  scope,
}: {
  capacity: number;
  listId: string;
  scope: string;
}) =>
  `
${scope}:not([data-expanded]) #${listId} > li {
  display: none;
}
${scope}:not([data-expanded]) #${listId} > li:nth-child(-n+${capacity}) {
  display: list-item;
}
`.trim();

const GridOverflowStyles = ({
  capacities,
  listId,
  scopeId,
}: GridOverflowStylesProps) => {
  const scope = `[data-grid-overflow-scope="${scopeId}"]`;
  const css = `
${buildViewportCapRules({
  capacity: capacities.small,
  listId,
  scope,
})}
@media (min-width: 768px) {
  ${buildViewportCapRules({
    capacity: capacities.medium,
    listId,
    scope,
  })}
  ${scope} [data-grid-overflow-reveal][data-fits-md] {
    display: none;
  }
}
@media (min-width: 1280px) {
  ${buildViewportCapRules({
    capacity: capacities.large,
    listId,
    scope,
  })}
  ${scope} [data-grid-overflow-reveal][data-fits-xl] {
    display: none;
  }
}
`.trim();

  return <style>{css}</style>;
};

const GridOverflow = ({
  buttonLabel,
  children,
  columnCount,
  columnLayout,
  rowCap,
}: GridOverflowProps) => {
  const [expanded, setExpanded] = useState(false);
  const isRevealVisible = useOverflowRevealAnimation(expanded);
  const reactId = useId().replace(/:/g, "");
  const listId = `grid-overflow-${reactId}`;
  useOverflowRevealFocus(expanded, listId);
  const childElements = Children.toArray(children).filter(
    (child): child is ReactElement =>
      isValidElement(child) && child.key != null,
  );
  const capacities = getOverflowCapacities({
    columnCount,
    columnLayout,
    rowCap,
  });

  return (
    <div
      data-expanded={expanded ? "" : undefined}
      data-grid-overflow-scope={reactId}
    >
      <GridOverflowStyles
        capacities={capacities}
        listId={listId}
        scopeId={reactId}
      />
      <UIGrid
        columnCount={columnCount}
        columnLayout={columnLayout}
        listId={listId}
      >
        <GridOverflowItems
          capacities={capacities}
          expanded={expanded}
          isRevealVisible={isRevealVisible}
          items={childElements}
        />
      </UIGrid>

      {expanded ? null : (
        <GridOverflowRevealControl
          buttonLabel={buttonLabel}
          capacities={capacities}
          itemCount={childElements.length}
          listId={listId}
          onReveal={() => setExpanded(true)}
        />
      )}

      {!expanded ? null : (
        <p aria-live="polite" className="sr-only" role="status">
          All {childElements.length} grid items are now visible.
        </p>
      )}
    </div>
  );
};

export { GridOverflow };
